import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  Eye,
  FileSpreadsheet,
  FileText,
  MoreVertical,
  Pencil,
  Plus,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { KpiCard, KpiGrid } from "../components/KpiCard";
import { SortableTableHead } from "../components/SortableTableHead";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import {
  allInvoiceStatusFilterOptions,
  getInvoiceDisplayStatus,
} from "../data";
import { useAdminInvoices } from "../hooks/useAdminInvoices";
import { DeadlineBadge, deadlineText } from "../components/DeadlineBadge";
import { cn } from "@/shared/lib/cn";
import type { InvoiceDisplayStatus } from "../types";
import { ExportMenu } from "../components/ExportMenu";

const statusBadgeClass: Record<InvoiceDisplayStatus, string> = {
  submitted: "bg-primary-soft text-primary",
  inReview: "bg-accent-soft text-accent-foreground",
  awaitingClaims: "bg-purple-soft text-purple",
  pendingAudit: "bg-accent-soft text-accent",
  approved: "bg-accent/15 text-accent",
  rejected: "bg-danger-soft text-danger",
};

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

type InvoiceActionsMenuProps = {
  isOpen: boolean;
  labels: { view: string; edit: string };
  onToggle: () => void;
  onClose: () => void;
  onView: () => void;
  onEdit: () => void;
};

function InvoiceActionsMenu({
  isOpen,
  labels,
  onToggle,
  onClose,
  onView,
  onEdit,
}: InvoiceActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
      >
        <MoreVertical size={14} />
      </button>

      {isOpen && (
        <div className="card-shadow absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-xl border border-border bg-card p-1.5">
          <button
            type="button"
            onClick={() => {
              onView();
              onClose();
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent/20"
          >
            <Eye size={14} className="text-primary" />
            {labels.view}
          </button>
          <button
            type="button"
            onClick={() => {
              onEdit();
              onClose();
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent/20"
          >
            <Pencil size={14} className="text-accent" />
            {labels.edit}
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

/** Échéance dépassée (facture non approuvée ni rejetée) ? */
function isOverdue(dueDate: string | null | undefined, status: InvoiceDisplayStatus): boolean {
  return Boolean(dueDate) && status !== "approved" && status !== "rejected" && String(dueDate).slice(0, 10) < new Date().toISOString().slice(0, 10);
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function AllInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.all;
  const { exportTable, printPage, navigate } = usePageActions();
  const { invoices: allInvoicesList, loading, error } = useAdminInvoices({ mode: "all" });

  const [topSearch, setTopSearch] = useState("");
  // Statut choisi depuis une carte du tableau de bord (?status=submitted, inReview…).
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") ?? "");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Filtres du cahier des charges : assureur, hôpital, entreprise, lot.
  const [place, setPlace] = useState({ insurer: "", hospital: "", company: "", batch: "" });
  const isAdmin = getStoredAccountType() === "admin";
  const { sortKey, sortDirection, toggleSort } = useTableSort("displayId", "desc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setStatusFilter("");
    setTableSearch("");
    setPlace({ insurer: "", hospital: "", company: "", batch: "" });
    setPage(1);
  };

  const choices = useMemo(() => {
    const unique = (values: Array<string | null | undefined>) =>
      [...new Set(values.filter((v): v is string => Boolean(v) && v !== "—"))].sort((a, b) => a.localeCompare(b));
    return {
      insurer: unique(allInvoicesList.map((invoice) => invoice.insurer)),
      hospital: unique(allInvoicesList.map((invoice) => invoice.hospital)),
      company: unique(allInvoicesList.map((invoice) => invoice.company)),
    };
  }, [allInvoicesList]);

  // Tableau de bord : total, en attente, approuvées, rejetées (cliquer filtre la liste).
  const counts = useMemo(() => {
    const result = { total: allInvoicesList.length, pending: 0, approved: 0, rejected: 0 };
    for (const invoice of allInvoicesList) {
      const status = getInvoiceDisplayStatus(invoice);
      if (status === "approved") result.approved += 1;
      else if (status === "rejected") result.rejected += 1;
      else result.pending += 1;
    }
    return result;
  }, [allInvoicesList]);

  const filtered = useMemo(() => {
    return allInvoicesList.filter((invoice) => {
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company} ${invoice.insurer ?? ""} ${invoice.batchNumber ?? ""} ${invoice.reference} ${invoice.submittedDate} ${invoice.invoiceNature ?? ""}`.toLowerCase();
      const displayStatus = getInvoiceDisplayStatus(invoice);

      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byStatus =
        statusFilter === "open"
          ? displayStatus !== "approved" && displayStatus !== "rejected"
          : statusFilter
            ? displayStatus === statusFilter
            : true;
      const byPlace =
        (!place.insurer || invoice.insurer === place.insurer) &&
        (!place.hospital || invoice.hospital === place.hospital) &&
        (!place.company || invoice.company === place.company) &&
        (!place.batch || (invoice.batchNumber ?? "").toLowerCase().includes(place.batch.toLowerCase()));

      return byTopSearch && byTableSearch && byStatus && byPlace;
    });
  }, [allInvoicesList, topSearch, statusFilter, tableSearch, place]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (invoice, key) => {
        if (key === "displayId") return invoice.displayId;
        if (key === "submittedDate") return invoice.submittedDate;
        if (key === "hospital") return invoice.hospital;
        if (key === "insurer") return invoice.insurer ?? "";
        if (key === "company") return invoice.company;
        if (key === "batch") return invoice.batchNumber ?? "";
        if (key === "amount") return invoice.amount;
        if (key === "due") return invoice.dueDate ?? "9999";
        if (key === "status") return getInvoiceDisplayStatus(invoice);
        return "";
      }),
    [filtered, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const columnDefs = [
    { key: "index", label: "#", sortable: false },
    { key: "displayId", label: labels.columns.id, sortable: true },
    { key: "insurer", label: "Assureur", sortable: true },
    { key: "hospital", label: labels.columns.hospital, sortable: true },
    { key: "company", label: "Entreprise", sortable: true },
    { key: "batch", label: "N° lot", sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "due", label: "Échéance", sortable: true },
    { key: "amount", label: labels.columns.amount, sortable: true },
    { key: "actions", label: labels.columns.actions, sortable: false },
  ] as const;

  const handleExportExcel = () => {
    exportTable(
      "factures.csv",
      [labels.columns.id, "Assureur", labels.columns.hospital, "Entreprise", "N° lot", labels.columns.status, "Échéance", "Niveau en cours", labels.columns.amount],
      sorted.map((invoice) => {
        const displayStatus = getInvoiceDisplayStatus(invoice);
        return [
          String(invoice.displayId),
          invoice.insurer ?? "",
          invoice.hospital,
          invoice.company,
          invoice.batchNumber ?? "",
          labels.statuses[displayStatus],
          formatDate(invoice.dueDate),
          deadlineText(invoice.deadline),
          formatAmount(invoice.amount),
        ];
      }),
    );
  };

  return (
    <>
      <Breadcrumbs homeHref={getDashboardHomeHref()} items={[{ label: labels.title }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
          {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : null}
          {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
        </div>
        {isAdmin ? (
          <Link
            to="/dashboard/invoices/submit"
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
          >
            <Plus size={16} />
            Nouvelle facture
          </Link>
        ) : null}
      </div>

      {/* 1re ligne : tableau de bord ; un clic filtre la liste. */}
      <KpiGrid className="mb-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {(
          [
            { id: "", label: "Total", value: counts.total, icon: FileText, tone: "purple" },
            { id: "open", label: "En attente", value: counts.pending, icon: Clock, tone: "amber" },
            { id: "approved", label: "Approuvées", value: counts.approved, icon: CheckCircle, tone: "mint" },
            { id: "rejected", label: "Rejetées", value: counts.rejected, icon: XCircle, tone: "red" },
          ] as const
        ).map((card) => (
          <KpiCard
            key={card.label}
            tone={card.tone}
            icon={card.icon}
            label={card.label}
            value={card.value}
            active={statusFilter === card.id && card.id !== ""}
            onClick={() => {
              setStatusFilter(card.id);
              setPage(1);
            }}
          />
        ))}
      </KpiGrid>

      {/* 2e ligne : filtres. */}
      <div className="header-ribbon filter-panel relative isolate rounded-[var(--radius-card)] mb-6 p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          {(
            [
              ["insurer", "Assureur", "Tous les assureurs"],
              ["hospital", "Hôpital", "Tous les hôpitaux"],
              ["company", "Entreprise", "Toutes les entreprises"],
            ] as const
          ).map(([key, label, all]) => (
            <FormField key={key} label={label}>
              <select
                value={place[key]}
                onChange={(event) => {
                  setPlace({ ...place, [key]: event.target.value });
                  setPage(1);
                }}
                className={selectClass}
              >
                <option value="">{all}</option>
                {choices[key].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </FormField>
          ))}
          <FormField label="N° lot">
            <input
              value={place.batch}
              onChange={(event) => {
                setPlace({ ...place, batch: event.target.value });
                setPage(1);
              }}
              placeholder="LOT-…"
              className={inputClass}
            />
          </FormField>
          <FormField label={labels.columns.status}>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">{labels.allStatuses}</option>
              <option value="open">En attente (non traitées)</option>
              {allInvoiceStatusFilterOptions
                .filter((option) => option.value)
                .map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
          </FormField>
        </div>
      </div>

      {/* 3e ligne : tableau. */}
      <div className="theme-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={topSearch}
              onChange={(event) => {
                setTopSearch(event.target.value);
                setPage(1);
              }}
              placeholder={labels.searchPlaceholder}
              className={`${inputClass} w-full min-w-[200px] max-w-xs`}
            />
          </div>
          <div className="flex items-center gap-2">
            <ExportMenu onExport={handleExportExcel} />
            <button
              type="button"
              onClick={printPage}
              className="inline-flex items-center gap-2 rounded-lg bg-danger px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-danger/90"
            >
              <FileText size={15} />
              {labels.exportPdf}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Rechercher…"
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
        />

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((invoice, index) => {
                const displayStatus = getInvoiceDisplayStatus(invoice);
                return (
                  <tr
                    key={invoice.id}
                    className={cn(
                      "border-b border-border last:border-0 hover:bg-card-muted/30",
                      index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                    )}
                  >
                    <td className="px-4 py-3 text-sm text-muted">{start + index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      <Link
                        to={`/dashboard/invoices/${invoice.displayId}`}
                        className="text-foreground transition-colors hover:text-primary"
                      >
                        {invoice.displayId}
                      </Link>
                      <span className="block text-xs text-muted">{invoice.submittedDate}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.insurer || "—"}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.hospital}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.company || "Plusieurs entreprises"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground">{invoice.batchNumber || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-current/20",
                          statusBadgeClass[displayStatus],
                        )}
                      >
                        {labels.statuses[displayStatus]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn("block text-sm", isOverdue(invoice.dueDate, displayStatus) ? "font-semibold text-red-700" : "text-foreground")}>
                        {formatDate(invoice.dueDate)}
                        {isOverdue(invoice.dueDate, displayStatus) ? " (dépassée)" : ""}
                      </span>
                      <DeadlineBadge deadline={invoice.deadline} />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {formatAmount(invoice.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <InvoiceActionsMenu
                        isOpen={openMenuId === invoice.id}
                        labels={labels.actions}
                        onToggle={() =>
                          setOpenMenuId((current) =>
                            current === invoice.id ? null : invoice.id,
                          )
                        }
                        onClose={() => setOpenMenuId(null)}
                        onView={() => navigate(`/dashboard/invoices/${invoice.displayId}`)}
                        onEdit={() => navigate(`/dashboard/invoices/${invoice.displayId}/edit`)}
                      />
                    </td>
                  </tr>
                );
              })}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing}{" "}
            {sorted.length === 0 ? 0 : start + 1} {labels.pagination.to}{" "}
            {Math.min(start + pageSize, sorted.length)} {labels.pagination.of}{" "}
            {sorted.length} {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setPage(pageNum)}
                className={cn(
                  "min-w-9 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                  pageNum === currentPage
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-muted-foreground hover:bg-accent/20 hover:border-accent",
                )}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
